import { Helmet } from "react-helmet-async";

const SITE_URL = "https://rebelky.online";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const DEFAULT_DESC =
  "Rebelky – česká dívčí kapela ze studia Pinkofein. Sofi, Agi, Leyla a Naty. " +
  "Pop hudba, energické koncerty po celé ČR, merch e-shop, novinky a Spotify. " +
  "Najdi termíny koncertů, kup tričko nebo sleduj nás na YouTube, TikToku a Instagramu.";

const DEFAULT_KEYWORDS =
  "Rebelky, kapela Rebelky, česká dívčí kapela, girl band, Sofi, Agi, Leyla, Naty, " +
  "Pinkofein, koncerty 2026, Bezvafest, dětská kapela, mladá kapela, pop CZ, " +
  "Rebelky merch, Rebelky Spotify, Rebelky YouTube, Rebelky TikTok, Rebelky Instagram, " +
  "kapela pro děti, koncert Praha, koncert Brno, koncert Hradec Králové";

export default function SEO({
  title,
  description,
  image,
  url,
  type = "website",
  keywords,
  noindex = false,
}) {
  const siteName = "Rebelky";
  const fullTitle = title
    ? `${title} | Rebelky – Česká dívčí kapela`
    : "Rebelky – Česká dívčí kapela | Sofi, Agi, Leyla & Naty";
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const desc = description || DEFAULT_DESC;
  const kw = keywords || DEFAULT_KEYWORDS;
  const img = image || DEFAULT_OG_IMAGE;

  // JSON-LD structured data – MusicGroup
  const musicGroupSchema = {
    "@context": "https://schema.org",
    "@type": "MusicGroup",
    name: "Rebelky",
    alternateName: "Kapela Rebelky",
    url: SITE_URL,
    logo: `${SITE_URL}/og-image.jpg`,
    image: img,
    description: DEFAULT_DESC,
    foundingDate: "2023-09",
    foundingLocation: { "@type": "Place", name: "Karlínské divadlo, Praha, Česká republika" },
    genre: ["Pop", "Czech Pop", "Girl Band"],
    member: [
      { "@type": "Person", name: "Sofi", roleName: "vokál" },
      { "@type": "Person", name: "Agi", roleName: "vokál" },
      { "@type": "Person", name: "Leyla", roleName: "vokál, tanec" },
      { "@type": "Person", name: "Naty", roleName: "vokál" },
    ],
    sameAs: [
      "https://www.instagram.com/rebelky_official/",
      "https://www.tiktok.com/@rebelky_official",
      "https://www.youtube.com/@kapela_rebelky",
      "https://open.spotify.com/artist/1igMI4JGH7TXcozyMzP48M",
      "https://www.kapelarebelky.cz/",
    ],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    url: SITE_URL,
    name: siteName,
    description: DEFAULT_DESC,
    inLanguage: "cs-CZ",
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <meta name="author" content="Rebelky / Pinkofein s.r.o." />
      <meta name="publisher" content="Pinkofein s.r.o." />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"}
      />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="3 days" />
      <meta name="geo.region" content="CZ" />
      <meta name="geo.placename" content="Česká republika" />
      <meta httpEquiv="Content-Language" content="cs-CZ" />
      <link rel="canonical" href={fullUrl} />
      <link rel="alternate" hrefLang="cs" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />
      <html lang="cs" />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Rebelky – česká dívčí kapela" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="cs_CZ" />
      <meta property="og:locale:alternate" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      <meta name="twitter:image:alt" content="Rebelky – česká dívčí kapela" />
      <meta name="twitter:site" content="@rebelky_official" />
      <meta name="twitter:creator" content="@rebelky_official" />

      {/* Mobile */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="theme-color" content="#FF005A" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Rebelky" />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(musicGroupSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
    </Helmet>
  );
}
